import { useSnackbar } from '@/hooks/common/useSnackbar'
import {
  Transporter,
  TransporterPackage,
  TransporterPackageStatus,
  TransporterPackageWithVehicles,
  TransporterStatus,
  TransporterWithPackages,
  TransporterGroup,
  Vehicle,
  TransporterPhoto,
  VehiclePhotoStatus,
  VehicleStatus,
  PickupLocation,
  DropoffLocation,
  RepairShopLocation,
  VehicleNeeds,
} from '@otw/models'
import { transporterConverter, transporterPackageConverter } from '@otw/models/converters'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { db, storage } from '@/config/firebase'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  WithFieldValue,
  limit,
  orderBy
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from '@firebase/storage'
import { VehicleFile } from '@otw/models/transporter'
import { GatePassFile } from '@otw/models/inventory'
import { useCallback, useMemo } from 'react'
import { apiClient } from '@/core/api/apiClient'
import { fileToBase64 } from '@/core/utils/fileUtils'
import { copyStorageFileToNewLocation } from '@/core/firebaseStorage/copyStorageFileToNewLocation'
import imageCompression from 'browser-image-compression'

export const useAdminTransporter = () => {
  const queryClient = useQueryClient()
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()

  // Collection references with converters
  const transportersCollection = collection(db, 'transporters')
  const packagesCollection = collection(db, 'transporter_packages').withConverter(transporterPackageConverter)

  // React Query hooks for data fetching
  const { data: transporters = [], isLoading: isLoadingTransporters } = useQuery({
    queryKey: ['transporters'],
    queryFn: async () => {
      const q = query(transportersCollection, orderBy('created_at', 'desc'))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => {
        const data = doc.data();
        // Manually apply converter logic
        return {
          id: doc.id,
          email: data.email || '',
          phone: data.phone,
          first_name: data.first_name,
          last_name: data.last_name,
          created_at: data.created_at,
          updated_at: data.updated_at,
          status: data.status as TransporterStatus,
          transporterOwnerId: data.transporterOwnerId,
          transporter_role: data.transporter_role,
          user_id: data.user_id || '',
          elevated: data.elevated || false,
          driver_license_front_storage_path: data.driver_license_front_storage_path,
        } as Transporter;
      });
    }
  })

  const { data: packages = [], isLoading: isLoadingPackages } = useQuery({
    queryKey: ['transporter_packages'],
    queryFn: async () => {
      const q = query(packagesCollection, orderBy('created_at', 'desc'))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as TransporterPackage))
    }
  })

  const { data: packagesWithVehicles = [], isLoading: isLoadingPackagesWithVehicles } = useQuery({
    queryKey: ['transporter_packages_with_vehicles'],
    staleTime: 30000, // Cache for 30 seconds to reduce refetches
    queryFn: async () => {
      const q = query(packagesCollection, orderBy('created_at', 'desc'))
      const snapshot = await getDocs(q)
      const packagesList: TransporterPackageWithVehicles[] = []

      // Step 1: Collect all unique vehicle IDs and transporter IDs first
      const allVehicleIds = new Set<string>()
      const allTransporterIds = new Set<string>()
      const packageDataMap = new Map<string, DocumentData>()

      for (const docSnapshot of snapshot.docs) {
        const packageData = docSnapshot.data() as DocumentData
        packageDataMap.set(docSnapshot.id, packageData)
        
        // Collect vehicle IDs
        if (packageData.vehicle_ids?.length > 0) {
          packageData.vehicle_ids.forEach((id: string) => allVehicleIds.add(id))
        }
        
        // Collect transporter IDs
        if (packageData.company_id) {
          allTransporterIds.add(packageData.company_id)
        }
      }

      // Step 2: Batch fetch all vehicles in parallel (Firestore handles batching internally)
      const vehiclePromises = Array.from(allVehicleIds).map(async (vehicleId: string) => {
        try {
          const vehicleDoc = await getDoc(doc(db, 'master_inventory', vehicleId))
          return vehicleDoc.exists() ? { id: vehicleDoc.id, ...vehicleDoc.data() } as Vehicle : null
        } catch (error) {
          console.error(`Error fetching vehicle ${vehicleId}:`, error)
          return null
        }
      })
      const vehicleResults = await Promise.all(vehiclePromises)
      const vehiclesMap = new Map<string, Vehicle>()
      vehicleResults.forEach(vehicle => {
        if (vehicle) {
          vehiclesMap.set(vehicle.id, vehicle)
        }
      })

      // Step 3: Batch fetch all transporters in parallel
      const transporterPromises = Array.from(allTransporterIds).map(async (transporterId: string) => {
        try {
          const transporterDoc = await getDoc(doc(transportersCollection, transporterId))
          if (transporterDoc.exists()) {
            const data = transporterDoc.data()
            return {
              id: transporterDoc.id,
              transporter: {
                id: transporterDoc.id,
                user_id: data.user_id || '',
                email: data.email || '',
                phone: data.phone,
                first_name: data.first_name,
                last_name: data.last_name,
                created_at: data.created_at as Timestamp,
                updated_at: data.updated_at as Timestamp,
                status: data.status as TransporterStatus,
                transporter_role: data.transporter_role,
                elevated: data.elevated || false,
                driver_license_front_storage_path: data.driver_license_front_storage_path,
              } as Transporter
            }
          }
          return null
        } catch (error) {
          console.error(`Error fetching transporter ${transporterId}:`, error)
          return null
        }
      })
      const transporterResults = await Promise.all(transporterPromises)
      const transportersMap = new Map<string, Transporter>()
      transporterResults.forEach(result => {
        if (result?.transporter) {
          transportersMap.set(result.id, result.transporter)
        }
      })

      // Step 4: Build packages with vehicles and transporters from maps
      for (const [packageId, packageData] of packageDataMap.entries()) {
        const packageWithVehicles: TransporterPackageWithVehicles = {
          id: packageId,
          name: packageData.name || '',
          description: packageData.description,
          created_at: packageData.created_at as Timestamp,
          updated_at: packageData.updated_at as Timestamp,
          created_by: packageData.created_by || '',
          client_user_id: (packageData.client_user_id as string) || '',
          status: packageData.status as TransporterPackageStatus,
          vehicle_ids: packageData.vehicle_ids || [],
          company_id: packageData.company_id || '',
          pickup_locations: packageData.pickup_locations,
          dropoff_locations: packageData.dropoff_locations,
          interim_locations: packageData.interim_locations,
          contact_status: packageData.contact_status || {},
          vehicle_status: packageData.vehicle_status || {},
          vehicle_assignments: packageData.vehicle_assignments || {},
          vehicle_priorities: packageData.vehicle_priorities || {},
          vehicle_ready: packageData.vehicle_ready || {},
          vehicle_damage: packageData.vehicle_damage || {},
          vehicle_needs: packageData.vehicle_needs || {},
          vehicle_files: packageData.vehicle_files || {},
          package_notes: packageData.package_notes || {},
          photo_upload_id: packageData.photo_upload_id,
          readable_id: packageData.readable_id,
          archive: packageData.archive || false,
          vehicles: [],
        }

        // Assign vehicles from map
        if (packageData.vehicle_ids?.length > 0) {
          packageWithVehicles.vehicles = packageData.vehicle_ids
            .map((vehicleId: string) => vehiclesMap.get(vehicleId))
            .filter((v: Vehicle | undefined): v is Vehicle => v !== undefined)
        }

        // Assign transporter from map
        if (packageData.company_id) {
          const transporter = transportersMap.get(packageData.company_id)
          if (transporter) {
            packageWithVehicles.transporter = transporter
          }
        }

        packagesList.push(packageWithVehicles)
      }

      return packagesList
    }
  })

  const { data: transportersWithPackages = [], isLoading: isLoadingTransportersWithPackages } = useQuery({
    queryKey: ['transporters_with_packages'],
    staleTime: 30000, // Cache for 30 seconds to reduce refetches
    queryFn: async () => {
      // Fetch transporters and packages in parallel
      const [transportersSnapshot, packagesSnapshot] = await Promise.all([
        getDocs(query(transportersCollection, orderBy('created_at', 'desc'))),
        getDocs(query(packagesCollection))
      ])

      // Build a map of transporter ID -> packages
      const packagesByTransporterId = new Map<string, TransporterPackage[]>()
      packagesSnapshot.docs.forEach(packageDoc => {
        // Converter already includes id field
        const packageData = packageDoc.data() as TransporterPackage
        const companyId = packageData.company_id
        if (companyId) {
          if (!packagesByTransporterId.has(companyId)) {
            packagesByTransporterId.set(companyId, [])
          }
          packagesByTransporterId.get(companyId)!.push(packageData)
        }
      })

      // Build transporters list with packages
      const transportersList: TransporterWithPackages[] = []
      transportersSnapshot.docs.forEach(docSnapshot => {
        const data = docSnapshot.data()
        const transporterData: Transporter = {
          id: docSnapshot.id,
          email: data.email || '',
          phone: data.phone,
          first_name: data.first_name,
          last_name: data.last_name,
          created_at: data.created_at,
          updated_at: data.updated_at,
          status: data.status as TransporterStatus,
          transporter_role: data.transporter_role,
          user_id: data.user_id || '',
          elevated: data.elevated || false,
          driver_license_front_storage_path: data.driver_license_front_storage_path,
        }
        const transporterWithPackages: TransporterWithPackages = {
          ...transporterData,
          packages: packagesByTransporterId.get(docSnapshot.id) || [],
        }
        transportersList.push(transporterWithPackages)
      })

      return transportersList
    }
  })

  const { data: transporterGroups = [], isLoading: isLoadingTransporterGroups } = useQuery({
    queryKey: ['transporter_groups'],
    queryFn: async () => {
      const q = query(collection(db, 'transporter_groups'), orderBy('created_at', 'desc'))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as TransporterGroup))
    }
  })

  // For vehicles in multiple packages, only consider the most recently created package per vehicle (excluding archived packages)
  const mostRecentPackageByVehicleId = useMemo(() => {
    const map = new Map<string, TransporterPackageWithVehicles>()
    for (const pkg of packagesWithVehicles) {
      if (pkg.archive) continue
      const createdMs = pkg.created_at?.toMillis?.() ?? (typeof (pkg.created_at as any)?.seconds === 'number' ? (pkg.created_at as any).seconds * 1000 : 0)
      for (const vehicleId of pkg.vehicle_ids ?? []) {
        const existing = map.get(vehicleId)
        const existingMs = existing?.created_at?.toMillis?.() ?? (existing?.created_at && typeof (existing.created_at as any)?.seconds === 'number' ? (existing.created_at as any).seconds * 1000 : 0)
        if (!existing || createdMs > existingMs) {
          map.set(vehicleId, pkg)
        }
      }
    }
    return map
  }, [packagesWithVehicles])

  const isLoading = isLoadingTransporters || isLoadingPackages || isLoadingPackagesWithVehicles || isLoadingTransportersWithPackages || isLoadingTransporterGroups


  // Create a new transporter mutation
  const createTransporterMutation = useMutation({
    mutationFn: async (transporterData: {
      user_id?: string
      email: string
      phone?: string
      first_name?: string
      last_name?: string
      transporterOwnerId?: string
    }) => {
      try {
        const newTransporter: any = {
          email: transporterData.email,
          phone: transporterData.phone || undefined,
          first_name: transporterData.first_name || undefined,
          last_name: transporterData.last_name || undefined,
          status: 'ACTIVE' as TransporterStatus,
          user_id: transporterData.user_id || '',
          created_at: Timestamp.now(),
          updated_at: Timestamp.now(),
        }

        // Only include transporterOwnerId if it exists
        if (transporterData.transporterOwnerId) {
          newTransporter.transporterOwnerId = transporterData.transporterOwnerId
        }

        // Use the converter directly which handles the typing correctly
        const docRef = await addDoc(transportersCollection, newTransporter)
        return docRef.id
      } catch (err) {
        console.error('Error creating transporter:', err)
        showError('Error creating transporter')
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transporters'] })
      queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
      showSuccess('Transporter created successfully')
    },
    onError: (err) => {
      console.error('Error creating transporter:', err)
      showError('Error creating transporter')
    }
  })

  // Create a new package mutation
  const createPackageMutation = useMutation({
    mutationFn: async (packageData: {
      name: string
      description?: string
      vehicle_ids: string[]
      company_id?: string
      created_by: string
      client_user_id?: string
      pickup_locations?: { [vehicleId: string]: PickupLocation }
      dropoff_locations?: { [vehicleId: string]: DropoffLocation }
      interim_locations?: { [vehicleId: string]: RepairShopLocation | RepairShopLocation[] }
      vehicle_priorities?: { [vehicleId: string]: boolean }
      vehicle_ready?: { [vehicleId: string]: { is_ready: boolean; ready_date: string } }
      vehicle_needs?: { [vehicleId: string]: VehicleNeeds }
      vehicle_files?: { [vehicleId: string]: File[] }
      vehicle_gate_passes?: { [vehicleId: string]: GatePassFile[] }
      inventoryCollection?: 'master_inventory' | 'shield_inventory'
      vehicle_status?: { [vehicleId: string]: VehicleStatus[] }
    }) => {
      // Clean up location data to ensure Firestore compatibility
      const cleanPickupLocations = packageData.pickup_locations ? 
        Object.entries(packageData.pickup_locations).reduce((acc, [vehicleId, location]) => {
          acc[vehicleId] = {
            address: location.address || '',
            city: location.city || '',
            state: location.state || '',
            zip: location.zip || '',
            contact_name: location.contact_name || '',
            contact_phone: location.contact_phone || '',
            notes: location.notes || ''
          };
          return acc;
        }, {} as { [vehicleId: string]: PickupLocation }) : null;

      const cleanDropoffLocations = packageData.dropoff_locations ? 
        Object.entries(packageData.dropoff_locations).reduce((acc, [vehicleId, location]) => {
          acc[vehicleId] = {
            address: location.address || '',
            city: location.city || '',
            state: location.state || '',
            zip: location.zip || '',
            contact_name: location.contact_name || '',
            contact_phone: location.contact_phone || '',
            notes: location.notes || ''
          };
          return acc;
        }, {} as { [vehicleId: string]: DropoffLocation }) : null;

      const cleanInterimLocations = packageData.interim_locations ? 
        Object.entries(packageData.interim_locations).reduce((acc, [vehicleId, locationOrArray]) => {
          // Handle both single location (for backward compatibility) and array
          const locations = Array.isArray(locationOrArray) ? locationOrArray : [locationOrArray];
          acc[vehicleId] = locations.map(location => ({
            address: location.address || '',
            city: location.city || '',
            state: location.state || '',
            zip: location.zip || '',
            contact_name: location.contact_name || '',
            contact_phone: location.contact_phone || '',
            notes: location.notes || '',
            pickup_date: location.pickup_date || undefined
          }));
          return acc;
        }, {} as { [vehicleId: string]: RepairShopLocation[] }) : null;

      // Check if this is a shop transport package
      const isShopTransport = packageData.company_id === 'shop';

      // Initialize vehicle status; start with any provided statuses so we can carry over history
      const initialVehicleStatus: { [vehicleId: string]: VehicleStatus[] } = packageData.vehicle_status
        ? { ...packageData.vehicle_status }
        : {};
      
      if (isShopTransport) {
        // Set all vehicles to "Repair" status for shop transport packages
        // Set current_location to dropoff_location for shop transport packages
        packageData.vehicle_ids.forEach(vehicleId => {
          // Use cleanDropoffLocations if available, otherwise fall back to packageData.dropoff_locations
          const dropoffLocation = cleanDropoffLocations?.[vehicleId] || packageData.dropoff_locations?.[vehicleId] as DropoffLocation | undefined;
          initialVehicleStatus[vehicleId] = [{
            status: 'Repair' as const,
            updated_at: Timestamp.now(),
            notes: 'Shop transport package - vehicle in repair',
            current_location: dropoffLocation
          }];
        });
      }

      // Handle client_user_id: use empty string if undefined or empty
      const clientUserId = packageData.client_user_id && typeof packageData.client_user_id === 'string' && packageData.client_user_id.trim() !== ''
        ? packageData.client_user_id
        : '';

      const newPackage: Omit<TransporterPackage, 'id'> = {
        name: packageData.name,
        description: packageData.description || '',
        vehicle_ids: packageData.vehicle_ids,
        company_id: packageData.company_id || '',
        created_at: Timestamp.now(),
        updated_at: Timestamp.now(),
        created_by: packageData.created_by,
        client_user_id: clientUserId,
        status: isShopTransport ? 'COMPLETED' as TransporterPackageStatus : 'DRAFT' as TransporterPackageStatus,
          pickup_locations: cleanPickupLocations || undefined,
          dropoff_locations: cleanDropoffLocations || undefined,
          interim_locations: cleanInterimLocations || undefined,
          contact_status: {},
          vehicle_status: initialVehicleStatus,
          vehicle_priorities: packageData.vehicle_priorities || undefined,
          vehicle_ready: packageData.vehicle_ready || undefined,
          vehicle_needs: packageData.vehicle_needs || undefined,
      }

      const docRef = await addDoc(packagesCollection, newPackage);
      const packageId = docRef.id;
      
      const vehicleFilesMap: { [vehicleId: string]: VehicleFile[] } = {};
      const inventoryCollection = packageData.inventoryCollection || 'master_inventory';
      
      // First, copy gate pass files from vehicles to package
      if (packageData.vehicle_gate_passes && Object.keys(packageData.vehicle_gate_passes).length > 0) {
        for (const [vehicleId, gatePassFiles] of Object.entries(packageData.vehicle_gate_passes)) {
          if (gatePassFiles && gatePassFiles.length > 0) {
            const copiedFiles: VehicleFile[] = [];
            
            for (const gatePassFile of gatePassFiles) {
              try {
                // Extract filename from storage path or use fileName
                const originalFileName = gatePassFile.fileName || gatePassFile.storagePath.split('/').pop() || 'file';
                const fileExtension = originalFileName.split('.').pop() || '';
                const baseFileName = originalFileName.substring(0, originalFileName.lastIndexOf('.')) || originalFileName;
                const sanitizedBaseName = baseFileName.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_{2,}/g, '_');
                const timestamp = Date.now();
                const fileName = `${timestamp}_${sanitizedBaseName}${fileExtension ? '.' + fileExtension : ''}`;
                
                const newStoragePath = `transporter_packages/${packageId}/files/${vehicleId}/${fileName}`;
                
                // Copy file from vehicle gate_passes location to package files location
                const newUrl = await copyStorageFileToNewLocation(gatePassFile.storagePath, newStoragePath);
                
                if (newUrl) {
                  // Convert GatePassFile to VehicleFile format
                  const vehicleFile: VehicleFile = {
                    fileName: gatePassFile.fileName,
                    storagePath: newStoragePath,
                    url: newUrl,
                    fileSize: gatePassFile.fileSize,
                    fileType: gatePassFile.fileType,
                    uploadedAt: Timestamp.now(),
                    uploadedBy: packageData.created_by,
                  };
                  
                  copiedFiles.push(vehicleFile);
                }
              } catch (error) {
                console.error(`Error copying gate pass file ${gatePassFile.fileName} for vehicle ${vehicleId}:`, error);
                showError(`Failed to copy gate pass file ${gatePassFile.fileName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
                // Continue with other files even if one fails
              }
            }
            
            if (copiedFiles.length > 0) {
              vehicleFilesMap[vehicleId] = copiedFiles;
            }
          }
        }
      }
      
      // Then, upload manually added files and merge with copied gate pass files
      if (packageData.vehicle_files && Object.keys(packageData.vehicle_files).length > 0) {
        
        // Upload files for each vehicle
        for (const [vehicleId, files] of Object.entries(packageData.vehicle_files)) {
          if (files && files.length > 0) {
            const uploadedFiles: VehicleFile[] = [];
            
            for (const file of files) {
              try {
                
                // Sanitize filename - remove special characters but keep extension
                const fileExtension = file.name.split('.').pop() || '';
                const baseFileName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
                // Remove all special characters except dots, hyphens, and underscores
                const sanitizedBaseName = baseFileName.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_{2,}/g, '_');
                const timestamp = Date.now();
                const fileName = `${timestamp}_${sanitizedBaseName}${fileExtension ? '.' + fileExtension : ''}`;
                
                const storagePath = `transporter_packages/${packageId}/files/${vehicleId}/${fileName}`;
                
                // Use storage directly (same pattern as useDocuments.ts)
                if (!storage) {
                  throw new Error('Firebase Storage is not initialized');
                }
                
                const storageRef = ref(storage, storagePath);
                
                // Upload file to Firebase Storage
                const uploadResult = await uploadBytes(storageRef, file);
                
                // Get download URL using the upload result reference
                const url = await getDownloadURL(uploadResult.ref);
                
                // Create file metadata
                const vehicleFile: VehicleFile = {
                  fileName: file.name,
                  storagePath,
                  url,
                  fileSize: file.size,
                  fileType: file.type,
                  uploadedAt: Timestamp.now(),
                  uploadedBy: packageData.created_by,
                };
                
                uploadedFiles.push(vehicleFile);
              } catch (error) {
                console.error(`Error uploading file ${file.name} for vehicle ${vehicleId}:`, error);
                console.error('Error details:', {
                  fileName: file.name,
                  fileSize: file.size,
                  fileType: file.type,
                  vehicleId,
                  packageId,
                  error: error instanceof Error ? error.message : String(error),
                  stack: error instanceof Error ? error.stack : undefined
                });
                showError(`Failed to upload file ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
                // Continue with other files even if one fails
              }
            }
            
            // Merge uploaded files with any existing copied gate pass files
            if (uploadedFiles.length > 0) {
              vehicleFilesMap[vehicleId] = [...(vehicleFilesMap[vehicleId] || []), ...uploadedFiles];
            }
          }
        }
      }
      
      // Update package with file metadata if any files were uploaded or copied
      if (Object.keys(vehicleFilesMap).length > 0) {
        await updateDoc(docRef, {
          vehicle_files: vehicleFilesMap,
          updated_at: Timestamp.now(),
        });
      } else {
        console.log('No vehicle files provided in package data');
      }
      
      return { ...newPackage, id: packageId }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
      showSuccess('Package created successfully')
    },
    onError: (err) => {
      console.error('Error creating package:', err)
      showError(`Error creating package: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  })

  // Assign a package to a transporter mutation
  const assignPackageToTransporterMutation = useMutation({
    mutationFn: async ({ packageId, transporterId }: { packageId: string; transporterId: string }) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)

        if (packageDoc.exists()) {
          const packageData = packageDoc.data() as TransporterPackage
          const vehicleIds = packageData.vehicle_ids || []

          // Set all vehicles to 'Assigned' status only if they have no status history
          const updatedSubmissionStatuses = { ...(packageData.vehicle_status || {}) }
          let hasExistingStatusHistory = false
          
          for (const vehicleId of vehicleIds) {
            const existingStatuses = updatedSubmissionStatuses[vehicleId] || []
            if (existingStatuses.length > 0) {
              hasExistingStatusHistory = true
            }
            if (existingStatuses.length === 0) {
              // Get pickup location for this vehicle
              const pickupLocation = packageData.pickup_locations?.[vehicleId] as PickupLocation | undefined
              
              const newStatusEntry: VehicleStatus = {
              status: 'Assigned' as const,
              updated_at: Timestamp.now(),
                notes: 'Package assigned to group',
                ...(pickupLocation && { current_location: pickupLocation })
            }
              updatedSubmissionStatuses[vehicleId] = [newStatusEntry]
            } else {
              // Preserve existing history; do not append additional entries
              updatedSubmissionStatuses[vehicleId] = existingStatuses
            }
          }

          // If any vehicle already has status history, treat this as a continuation and set package to IN_PROGRESS.
          // Otherwise, move from draft to ASSIGNED.
          const nextPackageStatus: TransporterPackageStatus = hasExistingStatusHistory ? 'IN_PROGRESS' : 'ASSIGNED'

          // Update the package with the transporter ID, status, and submission statuses in one call
          await updateDoc(packageRef, {
            company_id: transporterId,
            status: nextPackageStatus,
            vehicle_status: updatedSubmissionStatuses,
            updated_at: Timestamp.now(),
          })

          return true
        }

        throw new Error('Package not found')
      } catch (err) {
        console.error('Error assigning package to transporter:', err)
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transporters'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
      showSuccess('Package assigned to group successfully')
    },
    onError: (err) => {
      console.error('Error assigning package to transporter:', err)
      showError('Error assigning package to transporter')
    }
  })

  // Add transporter to package without changing status
  // This function only updates the company_id and does NOT add status entries
  // Status entries are only added when the package status changes to ASSIGNED
  const addTransporterToPackageMutation = useMutation({
    mutationFn: async ({ packageId, transporterId }: { packageId: string; transporterId: string }) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)

        if (packageDoc.exists()) {
          // Only update the company_id, do not add any status entries
          // Status entries are only added when package status changes to ASSIGNED
          await updateDoc(packageRef, {
            company_id: transporterId,
            updated_at: Timestamp.now(),
          })

          return true
        }

        throw new Error('Package not found')
      } catch (err) {
        console.error('Error adding transporter to package:', err)
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
    },
    onError: (err) => {
      console.error('Error adding transporter to package:', err)
      showError('Error adding transporter to package')
    }
  })

  // Update package status
  const updatePackageStatusMutation = useMutation({
    mutationFn: async ({ packageId, status }: { packageId: string; status: TransporterPackageStatus }) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)

        if (!packageDoc.exists()) {
          throw new Error('Package not found')
        }

        const packageData = packageDoc.data() as TransporterPackage
        const isOpticPackage = packageData.client_user_id === 'optic'
        const isMarkingCompleted = status === 'COMPLETED'

        // If marking package as completed, update vehicle statuses with current_location set to dropoff
        if (isMarkingCompleted) {
          const vehicleIds = packageData.vehicle_ids || []
          const updatedVehicleStatus: { [vehicleId: string]: VehicleStatus[] } = { ...(packageData.vehicle_status || {}) }

          vehicleIds.forEach(vehicleId => {
            const existingStatuses = updatedVehicleStatus[vehicleId] || []
            const dropoffLocation = packageData.dropoff_locations?.[vehicleId] as DropoffLocation | undefined
            
            if (isOpticPackage) {
              // For Optic packages, add "Repair" status entry when marking package as completed
            const newStatusEntry: VehicleStatus = {
              status: 'Repair' as const,
              updated_at: Timestamp.now(),
                notes: 'Vehicle advanced to repair shop by admin',
                current_location: dropoffLocation
            }
            updatedVehicleStatus[vehicleId] = [...existingStatuses, newStatusEntry]
            } else {
              // For customer packages, add "Completed" status entry with current_location set to dropoff
              const newStatusEntry: VehicleStatus = {
                status: 'Completed' as const,
                updated_at: Timestamp.now(),
                notes: 'Package marked as completed by admin',
                current_location: dropoffLocation
              }
              updatedVehicleStatus[vehicleId] = [...existingStatuses, newStatusEntry]
            }
          })

          // Update the package status and vehicle statuses
          await updateDoc(packageRef, {
            status,
            vehicle_status: updatedVehicleStatus,
            updated_at: Timestamp.now(),
          })
        } else {
          // Normal status update
          await updateDoc(packageRef, {
            status,
            updated_at: Timestamp.now(),
          })
        }

        return true
      } catch (err) {
        console.error('Error updating package status:', err)
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
      showSuccess('Package status updated successfully')
    },
    onError: (err) => {
      console.error('Error updating package status:', err)
      showError('Error updating package status')
    }
  })

  // Update contact status for a vehicle in a package
  const updateContactStatus = useCallback(
    async (
      packageId: string,
      vehicleId: string,
      contactStatus: {
        pickup_contacted?: boolean
        dropoff_contacted?: boolean
        pickup_confirmed?: boolean
        dropoff_confirmed?: boolean
      },
    ) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)

        if (packageDoc.exists()) {
          const packageData = packageDoc.data() as TransporterPackage
          const existingContactStatus = packageData.contact_status?.[vehicleId] || {}
          const updatedContactStatus = {
            ...existingContactStatus,
            ...contactStatus,
            last_updated: Timestamp.now(),
          }

          const updatedContactStatuses = {
            ...(packageData.contact_status || {}),
            [vehicleId]: updatedContactStatus,
          }

          await updateDoc(packageRef, {
            contact_status: updatedContactStatuses,
            updated_at: Timestamp.now(),
          })

          showSuccess('Contact status updated successfully')
          return true
        }

        showError('Package not found')
        return false
      } catch (err) {
        console.error('Error updating contact status:', err)
        showError('Error updating contact status')
        throw err
      }
    },
    [showSnackbar],
  )

  // Update pickup/dropoff/interim location for a vehicle in a package
  const updateVehicleLocation = useCallback(
    async (packageId: string, vehicleId: string, locationType: 'pickup' | 'dropoff' | 'interim', locationData: PickupLocation | DropoffLocation | RepairShopLocation, addNewRepairShop?: boolean) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)

        if (packageDoc.exists()) {
          const packageData = packageDoc.data() as TransporterPackage
          const locationsField = locationType === 'pickup' 
            ? 'pickup_locations' 
            : locationType === 'dropoff' 
              ? 'dropoff_locations' 
              : 'interim_locations'

          // Ensure we have a valid locations object to spread
          // Handle both undefined and null cases
          const existingLocations = packageData[locationsField] || {}

          // Clean the location data to remove undefined values
          // TypeScript knows locationData is already a valid location type, so we just filter out undefined
          const cleanLocationData: PickupLocation | DropoffLocation | RepairShopLocation = Object.fromEntries(
            Object.entries(locationData).filter(([_, value]) => value !== undefined)
          ) as PickupLocation | DropoffLocation | RepairShopLocation

          // For interim locations, either update the latest entry or append a new one
          // For others, replace
          let updatedLocations: any
          if (locationType === 'interim') {
            const existingInterim = existingLocations[vehicleId] || []
            const existingArray = Array.isArray(existingInterim) ? existingInterim : (existingInterim ? [existingInterim] : [])
            
            // If addNewRepairShop is true, append a new repair shop (for photo rejection flow)
            // Otherwise, update the latest repair shop entry
            if (addNewRepairShop === true) {
              // Add new repair shop to the array
              updatedLocations = {
                ...existingLocations,
                [vehicleId]: [...existingArray, cleanLocationData],
              }
            } else {
              // Update the latest repair shop entry
              if (existingArray.length > 0) {
                const updatedArray = [...existingArray]
                updatedArray[updatedArray.length - 1] = cleanLocationData
                updatedLocations = {
                  ...existingLocations,
                  [vehicleId]: updatedArray,
                }
              } else {
                // No existing repair shops, create new array with this location
                updatedLocations = {
                  ...existingLocations,
                  [vehicleId]: [cleanLocationData],
                }
              }
            }
          } else {
            updatedLocations = {
              ...existingLocations,
              [vehicleId]: cleanLocationData,
            }
          }

          // Create the update object with proper field initialization
          const updateData: any = {
            [locationsField]: updatedLocations,
            updated_at: Timestamp.now(),
          }

          await updateDoc(packageRef, updateData)

          // Invalidate queries to refresh the UI
          queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
          queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
          queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })

          // Don't show individual success messages when updating multiple locations
          return true
        }
        showError('Package not found')
        return false
      } catch (err) {
        console.error(`Error updating ${locationType} location:`, err)
        showError(`Error updating ${locationType} location: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Update the latest interim location for a vehicle (used for updating pickup_date)
  const updateLatestInterimLocation = useCallback(
    async (packageId: string, vehicleId: string, updates: Partial<RepairShopLocation>) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)

        if (packageDoc.exists()) {
          const packageData = packageDoc.data() as TransporterPackage
          const existingLocations = packageData.interim_locations || {}
          const existingInterim = existingLocations[vehicleId]

          if (!existingInterim) {
            showError('No interim location found for this vehicle')
            return false
          }

          // Handle both array and single location (for backward compatibility)
          const locations = Array.isArray(existingInterim) ? existingInterim : [existingInterim]
          
          if (locations.length === 0) {
            showError('No interim location found for this vehicle')
            return false
          }

          // Update the latest (last) location in the array
          const latestIndex = locations.length - 1
          const updatedLocations = [...locations]
          const updatedLocation = {
            ...updatedLocations[latestIndex],
            ...updates,
          }

          // Clean the updated location data to remove undefined values
          const cleanLocationData: RepairShopLocation = {
            address: updatedLocation.address || '',
            city: updatedLocation.city || '',
            state: updatedLocation.state || '',
            zip: updatedLocation.zip || '',
            ...(updatedLocation.contact_name ? { contact_name: updatedLocation.contact_name } : {}),
            ...(updatedLocation.contact_phone ? { contact_phone: updatedLocation.contact_phone } : {}),
            ...(updatedLocation.notes ? { notes: updatedLocation.notes } : {}),
            ...(updatedLocation.pickup_date ? { pickup_date: updatedLocation.pickup_date } : {}),
          }
          updatedLocations[latestIndex] = cleanLocationData

          const updateData: any = {
            interim_locations: {
              ...existingLocations,
              [vehicleId]: updatedLocations,
            },
            updated_at: Timestamp.now(),
          }

          await updateDoc(packageRef, updateData)

          // Invalidate queries to refresh the UI
          queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
          queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
          queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })

          return true
        }
        showError('Package not found')
        return false
      } catch (err) {
        console.error('Error updating latest interim location:', err)
        showError(`Error updating interim location: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )


  // Update submission status for a vehicle in a package with automatic status progression
  const updateSubmissionStatus = useCallback(
    async (
      packageId: string,
      vehicleId: string,
      newStatus: 'Assigned' | 'In Transit' | 'Completed' | 'Repair',
      notes: string = '',
      currentLocation?: PickupLocation | DropoffLocation | RepairShopLocation,
    ) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)

        if (packageDoc.exists()) {
          const packageData = packageDoc.data() as TransporterPackage

          // Get existing submission statuses for this vehicle
          const existingSubmissionStatuses = packageData.vehicle_status?.[vehicleId] || []
          
          // Create new status entry - only include current_location if it's defined
          const newStatusEntry: VehicleStatus = {
            status: newStatus,
            updated_at: Timestamp.now(),
            notes: notes,
            ...(currentLocation && { current_location: currentLocation })
          }

          // Add the new status to the array
          const updatedSubmissionStatuses = {
            ...(packageData.vehicle_status || {}),
            [vehicleId]: [...existingSubmissionStatuses, newStatusEntry],
          }

          // Check if we need to automatically change package status
          let packageStatusUpdate = {}
          
          // If vehicle moves to 'In Transit', check if all vehicles are in 'In Transit' or 'Completed'
          if (newStatus === 'In Transit') {
            const vehicleIds = packageData.vehicle_ids || []
            const allVehiclesInTransitOrCompleted = vehicleIds.length > 0 && vehicleIds.every(vId => {
              const vehicleStatuses = updatedSubmissionStatuses[vId] || []
              const latestVehicleStatus = vehicleStatuses.length > 0 ? vehicleStatuses[vehicleStatuses.length - 1] : null
              return latestVehicleStatus?.status === 'In Transit' || latestVehicleStatus?.status === 'Completed'
            })
            
            if (allVehiclesInTransitOrCompleted && packageData.status === 'ASSIGNED') {
              packageStatusUpdate = {
                status: 'IN_PROGRESS',
                updated_at: Timestamp.now(),
              }
            }
          }

          // If vehicle moves to 'Completed', check if all vehicles are 'Completed'
          if (newStatus === 'Completed') {
            const vehicleIds = packageData.vehicle_ids || []
            const allVehiclesCompleted = vehicleIds.length > 0 && vehicleIds.every(vId => {
              const vehicleStatuses = updatedSubmissionStatuses[vId] || []
              const latestVehicleStatus = vehicleStatuses.length > 0 ? vehicleStatuses[vehicleStatuses.length - 1] : null
              return latestVehicleStatus?.status === 'Completed'
            })
            
            if (allVehiclesCompleted && (packageData.status === 'ASSIGNED' || packageData.status === 'IN_PROGRESS')) {
              packageStatusUpdate = {
                status: 'COMPLETED',
                updated_at: Timestamp.now(),
              }
            }
          }

          // Update the package with submission status and potentially status change
          await updateDoc(packageRef, {
            vehicle_status: updatedSubmissionStatuses,
            updated_at: Timestamp.now(),
            ...packageStatusUpdate,
          })


          return true
        }

        showError('Package not found')
        return false
      } catch (err) {
        console.error('Error updating submission status:', err)
        showError('Error updating submission status')
        throw err
      }
    },
    [showSnackbar],
  )

  // Fetch photo status for a specific vehicle in a package
  const fetchVehiclePhotoStatus = useCallback(
    async (packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' = 'preDelivery'): Promise<VehiclePhotoStatus | null> => {
      try {
        // Get photo task templates to determine required photos
        const taskTemplatesQuery = query(
          collection(db, 'photo_task_templates'),
          where('id', '==', 'default'),
          limit(1)
        )
        const taskTemplatesSnapshot = await getDocs(taskTemplatesQuery)
        
        if (taskTemplatesSnapshot.empty) {
          console.warn('Default photo task template not found')
          return null
        }

        const taskTemplate = taskTemplatesSnapshot.docs[0].data()
        const requiredAngles = taskTemplate.tasks?.filter((task: any) => task.active === true).map((task: any) => task.displayName) || []

        // Get photos for this vehicle filtered by delivery status
        const photosQuery = query(
          collection(db, 'transporter_packages', packageId, 'photos'),
          where('vehicleId', '==', vehicleId),
          where('deliveryStatus', '==', deliveryStatus)
        )
        const photosSnapshot = await getDocs(photosQuery)

        const uploadedAngles = photosSnapshot.docs.map(doc => doc.data().angle).filter(Boolean)
        const completedAngles = requiredAngles.filter((angle: string) => uploadedAngles.includes(angle))
        
        const isComplete = completedAngles.length === requiredAngles.length
        const completionPercentage = requiredAngles.length > 0 ? (completedAngles.length / requiredAngles.length) * 100 : 0

        // Get package data to check submission status
        const packageDoc = await getDoc(doc(db, 'transporter_packages', packageId))
        const packageData = packageDoc.data() as TransporterPackage
        const submissionStatuses = packageData.vehicle_status?.[vehicleId] || []
        const latestStatus = submissionStatuses.length > 0 ? submissionStatuses[submissionStatuses.length - 1] : null
        
        // Check if vehicle has progressed through statuses
        const preDeliveryComplete = latestStatus?.status === 'In Transit' || latestStatus?.status === 'Completed'
        const postDeliveryComplete = latestStatus?.status === 'Completed'

        return {
          vehicleId,
          packageId,
          isComplete,
          completionPercentage: Math.round(completionPercentage),
          requiredPhotos: requiredAngles.length,
          completedPhotos: completedAngles.length,
          missingPhotos: requiredAngles.filter((angle: string) => !uploadedAngles.includes(angle)),
          completedAngles,
          preDeliveryComplete,
          postDeliveryComplete
        }
      } catch (err) {
        console.error('Error fetching vehicle photo status:', err)
        throw err
      }
    },
    []
  )


  // Delete a package
  const deletePackage = useCallback(
    async (packageId: string) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)

        if (packageDoc.exists()) {
          const packageData = packageDoc.data() as TransporterPackage
          const vehicleIds = packageData.vehicle_ids || []
          
          // Create a batch for all deletions
          const batch = writeBatch(db)
          
          // 1. Delete all photos in the package's photos subcollection
          const photosCollection = collection(db, 'transporter_packages', packageId, 'photos')
          const photosSnapshot = await getDocs(photosCollection)
          photosSnapshot.docs.forEach(photoDoc => {
            batch.delete(photoDoc.ref)
          })
          
          // 2. Delete all videos in the package's videos subcollection
          const videosCollection = collection(db, 'transporter_packages', packageId, 'videos')
          const videosSnapshot = await getDocs(videosCollection)
          videosSnapshot.docs.forEach(videoDoc => {
            batch.delete(videoDoc.ref)
          })
          
        
          
          // 3. Delete the package itself
          batch.delete(packageRef)
          
          // Execute all deletions in one batch
          await batch.commit()

          // Invalidate all relevant queries to refresh the UI
          queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
          queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
          queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
          queryClient.invalidateQueries({ queryKey: ['transporter_packages', packageId, 'photos'] })
          queryClient.invalidateQueries({ queryKey: ['transporter_packages', packageId, 'videos'] })

          showSuccess('Package and all photos/videos from all vehicles deleted successfully')
          return true
        }

        showError('Package not found')
        return false
      } catch (err) {
        console.error('Error deleting package:', err)
        showError('Error deleting package')
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Delete a transporter
  const deleteTransporter = useCallback(
    async (transporterId: string) => {
      try {
        const transporterRef = doc(db, 'transporters', transporterId)
        const transporterDoc = await getDoc(transporterRef)

        if (transporterDoc.exists()) {
          const transporterData = transporterDoc.data()
          const userId = transporterData?.user_id

          await deleteDoc(transporterRef)

          // Delete the associated user if user_id exists
          if (userId) {
            try {
              await apiClient.portal.admin.deleteFirebaseUser(userId)
            } catch (userDeleteError) {
              console.error('Error deleting associated user:', userDeleteError)
              // Don't throw here as the transporter is already deleted
              showWarning('Transporter deleted but there was an issue deleting the associated user account')
            }
          }

          // Invalidate all relevant queries to refresh the UI
          queryClient.invalidateQueries({ queryKey: ['transporters'] })
          queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
          queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
          queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })

          showSuccess('Transporter deleted successfully')
          return true
        }

        showError('Transporter not found')
        return false
      } catch (err) {
        console.error('Error deleting transporter:', err)
        showError('Error deleting transporter')
        throw err
      }
    },
    [showSnackbar],
  )

  /** Removes `transporters/{id}` without deleting the Auth account (e.g. transport access revoked). */
  const deleteTransporterProfileOnly = useCallback(
    async (transporterId: string) => {
      const transporterRef = doc(db, 'transporters', transporterId)
      const transporterDoc = await getDoc(transporterRef)

      if (!transporterDoc.exists()) {
        return false
      }

      await deleteDoc(transporterRef)

      queryClient.invalidateQueries({ queryKey: ['transporters'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_groups'] })

      return true
    },
    [queryClient],
  )

  /** Deletes `repair_users` if present, then removes transporter profile (if any) and Firebase `users`/Auth, or only Auth if repair-only. */
  const deleteLogisticsUserCompletely = useCallback(
    async (userId: string) => {
      try {
        const repairRef = doc(db, 'repair_users', userId)
        const repairSnap = await getDoc(repairRef)
        if (repairSnap.exists()) {
          await deleteDoc(repairRef)
        }
        await queryClient.invalidateQueries({ queryKey: ['repair_users'] })

        const transporterRef = doc(db, 'transporters', userId)
        const transporterSnap = await getDoc(transporterRef)
        if (transporterSnap.exists()) {
          await deleteTransporter(userId)
          await queryClient.invalidateQueries({ queryKey: ['users'] })
          return true
        }

        await apiClient.portal.admin.deleteFirebaseUser(userId)
        await queryClient.invalidateQueries({ queryKey: ['users'] })
        showSuccess('User deleted successfully')
        return true
      } catch (err) {
        console.error('Error deleting logistics user:', err)
        showError('Error deleting user')
        throw err
      }
    },
    [deleteTransporter, queryClient, showError, showSuccess],
  )

  // Update an existing transporter
  const updateTransporter = useCallback(
    async (
      transporterId: string,
      transporterData: {
        email?: string
        phone?: string
        first_name?: string
        last_name?: string
        status?: string
        user_id?: string
        transporterOwnerId?: string
        transporter_role?: 'driver' | 'admin' | 'owner'
        elevated?: boolean
        driver_license_front_storage_path?: string
      },
    ) => {
      try {
        const transporterRef = doc(db, 'transporters', transporterId)

        await updateDoc(transporterRef, {
          ...transporterData,
          updated_at: Timestamp.now(),
        })

        showSuccess('Transporter updated successfully')
        return true
      } catch (err) {
        console.error('Error updating transporter:', err)
        showError('Error updating transporter')
        throw err
      }
    },
    [showSnackbar],
  )

  /** Creates or merges a `transporters/{id}` doc (e.g. logistics user gains transport access without an existing row). */
  const mergeCreateTransporterDocument = useCallback(
    async (transporterId: string, transporterData: Record<string, unknown>) => {
      await setDoc(
        doc(db, 'transporters', transporterId),
        {
          user_id: transporterId,
          created_at: Timestamp.now(),
          ...transporterData,
        },
        { merge: true },
      )
    },
    [],
  )

  // Create a new transporter company mutation
  const createTransporterGroupMutation = useMutation({
    mutationFn: async (groupData: {
      name: string
      owner_id: string
      driver_ids: string[]
      admin_ids: string[]
      price_per_mile?: number
    }) => {
      try {
        const newGroup: Omit<TransporterGroup, 'id'> = {
          name: groupData.name,
          owner_id: groupData.owner_id,
          driver_ids: groupData.driver_ids,
          admin_ids: groupData.admin_ids,
          price_per_mile: groupData.price_per_mile,
          created_at: Timestamp.now(),
          updated_at: Timestamp.now(),
        }

        const docRef = await addDoc(collection(db, 'transporter_groups'), newGroup)
        return docRef.id
      } catch (err) {
        console.error('Error creating transporter company:', err)
        showError('Error creating transporter company')
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transporter_groups'] })
      showSuccess('Transporter company created successfully')
    },
    onError: (err) => {
      console.error('Error creating transporter company:', err)
      showError('Error creating transporter company')
    }
  })

  // Update transporter company mutation
  const updateTransporterGroupMutation = useMutation({
    mutationFn: async ({ 
      groupId, 
      driverIds, 
      adminIds,
      pricePerMile
    }: { 
      groupId: string; 
      driverIds: string[];
      adminIds: string[];
      pricePerMile?: number;
    }) => {
      try {
        const groupRef = doc(db, 'transporter_groups', groupId)
        const updateData: any = {
          driver_ids: driverIds,
          admin_ids: adminIds,
          updated_at: Timestamp.now(),
        }
        
        // Only include price_per_mile if it's provided
        if (pricePerMile !== undefined) {
          updateData.price_per_mile = pricePerMile
        }
        
        await updateDoc(groupRef, updateData)
        return true
      } catch (err) {
        console.error('Error updating transporter company:', err)
        showError('Error updating transporter company')
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transporter_groups'] })
      showSuccess('Transporter company updated successfully')
    },
    onError: (err) => {
      console.error('Error updating transporter company:', err)
      showError('Error updating transporter company')
    }
  })

  // Delete transporter company mutation
  const deleteTransporterGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      try {
        const groupRef = doc(db, 'transporter_groups', groupId)
        await deleteDoc(groupRef)
        return true
      } catch (err) {
        console.error('Error deleting transporter company:', err)
        showError('Error deleting transporter company')
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transporter_groups'] })
      showSuccess('Transporter company deleted successfully')
    },
    onError: (err) => {
      console.error('Error deleting transporter company:', err)
      showError('Error deleting transporter company')
    }
  })

  // Check if a vehicle has meaningful pickup location data
  const hasVehiclePickupLocation = useCallback(
    (packageData: TransporterPackage, vehicleId: string): boolean => {
      const pickup = packageData.pickup_locations?.[vehicleId]
      return !!(pickup && (
        pickup.address?.trim() || 
        pickup.city?.trim() || 
        pickup.state?.trim() || 
        pickup.zip?.trim()
      ))
    },
    []
  )

  // Check if a vehicle has meaningful dropoff location data
  const hasVehicleDropoffLocation = useCallback(
    (packageData: TransporterPackage, vehicleId: string): boolean => {
      const dropoff = packageData.dropoff_locations?.[vehicleId]
      return !!(dropoff && (
        dropoff.address?.trim() || 
        dropoff.city?.trim() || 
        dropoff.state?.trim() || 
        dropoff.zip?.trim()
      ))
    },
    []
  )

  // Check if a vehicle has meaningful interim location data
  const hasVehicleInterimLocation = useCallback(
    (packageData: TransporterPackage, vehicleId: string): boolean => {
      const interim = packageData.interim_locations?.[vehicleId]
      if (!interim) return false
      
      // Handle both array and single location (for backward compatibility)
      const locations = Array.isArray(interim) ? interim : [interim]
      return locations.some(loc => (
        loc.address?.trim() || 
        loc.city?.trim() || 
        loc.state?.trim() || 
        loc.zip?.trim()
      ))
    },
    []
  )

  // Check if a vehicle has both pickup and dropoff locations
  const hasVehicleBothLocations = useCallback(
    (packageData: TransporterPackage, vehicleId: string): boolean => {
      return hasVehiclePickupLocation(packageData, vehicleId) && 
             hasVehicleDropoffLocation(packageData, vehicleId)
    },
    [hasVehiclePickupLocation, hasVehicleDropoffLocation]
  )

  // Normalize location address fields for comparison (trim + lower)
  const normalizeLoc = (loc: { address?: string; city?: string; state?: string; zip?: string }) => ({
    address: (loc.address ?? '').trim().toLowerCase(),
    city: (loc.city ?? '').trim().toLowerCase(),
    state: (loc.state ?? '').trim().toLowerCase(),
    zip: (loc.zip ?? '').trim().toLowerCase(),
  })

  // Compare two locations (repair shop or any location with address). Same id, or same address+city+state+zip.
  const isSameRepairShopLocation = useCallback(
    (
      a: { id?: string; address?: string; city?: string; state?: string; zip?: string },
      b: { id?: string; address?: string; city?: string; state?: string; zip?: string }
    ): boolean => {
      if (a.id && b.id) return a.id === b.id
      const na = normalizeLoc(a)
      const nb = normalizeLoc(b)
      return na.address === nb.address && na.city === nb.city && na.state === nb.state && na.zip === nb.zip
    },
    []
  )

  // Count trucks currently at a repair shop: non-completed, non-archived packages; vehicle_status latest entry is 'Repair' and current_location matches shop.
  const getRepairShopOccupancy = useCallback(
    (
      repairShop: RepairShopLocation | { id?: string; address?: string; city?: string; state?: string; zip?: string; capacity?: number }
    ): { count: number; capacity?: number } => {
      const capacity = 'capacity' in repairShop ? repairShop.capacity : undefined
      let count = 0
      for (const pkg of packagesWithVehicles) {
        if (pkg.status === 'COMPLETED' || pkg.archive) continue
        const statusMap = pkg.vehicle_status ?? {}
        for (const vehicleId of pkg.vehicle_ids ?? []) {
          const entries = statusMap[vehicleId]
          if (!entries || entries.length === 0) continue
          const sorted = [...entries].sort((x, y) => {
            const tx = x.updated_at?.toMillis?.() ?? 0
            const ty = y.updated_at?.toMillis?.() ?? 0
            return ty - tx
          })
          const latest = sorted[0]
          if (latest.status !== 'Repair' || !latest.current_location) continue
          const loc = latest.current_location
          if (!('address' in loc) || !('city' in loc) || !('state' in loc) || !('zip' in loc)) continue
          if (isSameRepairShopLocation(loc, repairShop)) count += 1
        }
      }
      return { count, capacity }
    },
    [packagesWithVehicles, isSameRepairShopLocation]
  )

  // Check if a vehicle is already in a package
  const getVehiclePackageInfo = useCallback(
    (vehicleId: string): { isInPackage: boolean; packageName?: string; packageId?: string; packageType?: 'customer' | 'optic'; packageStatus?: TransporterPackageStatus } => {
      const vehiclePackage = packages.find(pkg => pkg.vehicle_ids?.includes(vehicleId))
      const packageType = vehiclePackage?.client_user_id === 'optic' ? 'optic' : vehiclePackage?.client_user_id ? 'customer' : undefined
      return {
        isInPackage: !!vehiclePackage,
        packageName: vehiclePackage?.name,
        packageId: vehiclePackage?.id,
        packageType,
        packageStatus: vehiclePackage?.status
      }
    },
    [packages]
  )

  // Archive a package
  const archivePackage = useCallback(
    async (packageId: string) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        
        await updateDoc(packageRef, {
          archive: true,
          updated_at: Timestamp.now(),
        })
        
        // Refresh packages to show updated data
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        
        return true
      } catch (err) {
        console.error('Error archiving package:', err)
        showError('Error archiving package')
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Get location completion count for a package
  const getLocationCompletionCount = useCallback(
    (packageData: TransporterPackage | TransporterPackageWithVehicles): { completed: number; total: number } => {
      // Handle both TransporterPackage (with vehicle_ids) and TransporterPackageWithVehicles (with vehicles array)
      const vehicleIds = 'vehicles' in packageData 
        ? packageData.vehicles.map(v => v.id)
        : packageData.vehicle_ids || []
      
      const totalVehicles = vehicleIds.length
      const completedVehicles = vehicleIds.filter(vehicleId => 
        hasVehicleBothLocations(packageData, vehicleId)
      ).length
      
      return { completed: completedVehicles, total: totalVehicles }
    },
    [hasVehicleBothLocations]
  )

  // Fetch photos for a specific vehicle in a package (both pre-delivery and post-delivery)
  const fetchVehiclePhotos = useCallback(
    async (packageId: string, vehicleId: string): Promise<{ preDelivery: TransporterPhoto[], postDelivery: TransporterPhoto[] }> => {
      try {
        const [preDeliverySnapshot, postDeliverySnapshot] = await Promise.all([
          getDocs(query(
            collection(db, 'transporter_packages', packageId, 'photos'),
            where('vehicleId', '==', vehicleId),
            where('deliveryStatus', '==', 'preDelivery')
          )),
          getDocs(query(
            collection(db, 'transporter_packages', packageId, 'photos'),
            where('vehicleId', '==', vehicleId),
            where('deliveryStatus', '==', 'postDelivery')
          ))
        ])
        
        return {
          preDelivery: preDeliverySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as TransporterPhoto)),
          postDelivery: postDeliverySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as TransporterPhoto))
        }
      } catch (err) {
        console.error('Error fetching vehicle photos:', err)
        throw err
      }
    },
    []
  )

  // Update vehicles in a package
  const updatePackageVehicles = useCallback(
    async (packageId: string, vehicleIds: string[]) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)
        
        if (!packageDoc.exists()) {
          showError('Package not found')
          return
        }

        const packageData = packageDoc.data() as TransporterPackage
        const currentVehicleIds = packageData.vehicle_ids || []
        const currentVehicleStatus = packageData.vehicle_status || {}
        
        // Find vehicles that were removed
        const removedVehicles = currentVehicleIds.filter(id => !vehicleIds.includes(id))
        // Find vehicles that were added
        const addedVehicles = vehicleIds.filter(id => !currentVehicleIds.includes(id))
        
        // Create updated vehicle status object
        const updatedVehicleStatus: { [vehicleId: string]: VehicleStatus[] } = {}
        
        // Keep status for existing vehicles
        vehicleIds.forEach(vehicleId => {
          if (currentVehicleStatus[vehicleId]) {
            updatedVehicleStatus[vehicleId] = currentVehicleStatus[vehicleId]
          }
        })
        
        // Add new vehicles with 'Assigned' status if package is assigned to a transporter
        if (packageData.company_id && addedVehicles.length > 0) {
          addedVehicles.forEach(vehicleId => {
            const newStatusEntry = {
              status: 'Assigned' as const,
              updated_at: Timestamp.now(),
              notes: 'Vehicle added to package'
            }
            updatedVehicleStatus[vehicleId] = [newStatusEntry]
          })
        }
        
        // Update the package with new vehicle IDs and cleaned vehicle status
        await updateDoc(packageRef, {
          vehicle_ids: vehicleIds,
          vehicle_status: updatedVehicleStatus,
          updated_at: serverTimestamp(),
        })

        // Show appropriate success message
        if (removedVehicles.length > 0 && addedVehicles.length > 0) {
          showSuccess(`Package updated: ${removedVehicles.length} vehicle(s) removed, ${addedVehicles.length} vehicle(s) added`)
        } else if (removedVehicles.length > 0) {
          showSuccess(`${removedVehicles.length} vehicle(s) removed from package`)
        } else if (addedVehicles.length > 0) {
          showSuccess(`${addedVehicles.length} vehicle(s) added to package`)
        } else {
          showSuccess('Package vehicles updated successfully')
        }
        
        // Refresh packages to show updated data
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
      } catch (err) {
        console.error('Error updating package vehicles:', err)
        showError(`Error updating package vehicles: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Update vehicle priority in a package
  const updateVehiclePriority = useCallback(
    async (packageId: string, vehicleId: string, isHighPriority: boolean) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)
        
        if (!packageDoc.exists()) {
          showError('Package not found')
          return false
        }

        const packageData = packageDoc.data() as TransporterPackage
        const currentPriorities = packageData.vehicle_priorities || {}
        
        // Update the priority for the specific vehicle
        const updatedPriorities = {
          ...currentPriorities,
          [vehicleId]: isHighPriority
        }
        
        // Remove the vehicle from priorities if it's not high priority
        if (!isHighPriority) {
          delete updatedPriorities[vehicleId]
        }
        
        // Update the package with new vehicle priorities
        await updateDoc(packageRef, {
          vehicle_priorities: updatedPriorities,
          updated_at: Timestamp.now(),
        })

        // Show success message
        showSuccess(`Vehicle ${isHighPriority ? 'marked as high priority' : 'removed from high priority'}`)
        
        // Refresh packages to show updated data
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        
        return true
      } catch (err) {
        console.error('Error updating vehicle priority:', err)
        showError(`Error updating vehicle priority: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Update vehicle ready status in a package
  const updateVehicleReady = useCallback(
    async (packageId: string, vehicleId: string, isReady: boolean, readyDate?: string) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)
        
        if (!packageDoc.exists()) {
          showError('Package not found')
          return false
        }

        const packageData = packageDoc.data() as TransporterPackage
        const currentReady = packageData.vehicle_ready || {}
        
        // Set today's date if marking as ready and no date provided, otherwise use provided date or 'TBD'
        const finalReadyDate = isReady 
          ? (readyDate || new Date().toISOString().split('T')[0])
          : (readyDate || 'TBD')
        
        // Update the ready status for the specific vehicle
        const updatedReady = {
          ...currentReady,
          [vehicleId]: { is_ready: isReady, ready_date: finalReadyDate }
        }
        
        // Update the package with new vehicle ready status
        await updateDoc(packageRef, {
          vehicle_ready: updatedReady,
          updated_at: Timestamp.now(),
        })

        // Show success message
        showSuccess(`Vehicle ${isReady ? 'marked as ready for pickup' : 'removed from ready for pickup'}`)
        
        // Refresh packages to show updated data
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        
        return true
      } catch (err) {
        console.error('Error updating vehicle ready status:', err)
        showError(`Error updating vehicle ready status: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Update multiple vehicle ready dates in a package
  const updateMultipleVehicleReadyDates = useCallback(
    async (packageId: string, vehicleReadyDates: { [vehicleId: string]: { is_ready: boolean; ready_date: string } }) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)
        
        if (!packageDoc.exists()) {
          showError('Package not found')
          return false
        }

        const packageData = packageDoc.data() as TransporterPackage
        const currentReady = packageData.vehicle_ready || {}
        
        // Merge the new ready dates with existing ones
        const updatedReady = {
          ...currentReady,
          ...vehicleReadyDates
        }
        
        // Update the package with new vehicle ready statuses
        await updateDoc(packageRef, {
          vehicle_ready: updatedReady,
          updated_at: Timestamp.now(),
        })

        // Show success message
        const updatedCount = Object.keys(vehicleReadyDates).length
        showSuccess(`${updatedCount} vehicle${updatedCount !== 1 ? 's' : ''} pickup date${updatedCount !== 1 ? 's' : ''} updated successfully`)
        
        // Refresh packages to show updated data
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        
        return true
      } catch (err) {
        console.error('Error updating vehicle ready dates:', err)
        showError(`Error updating vehicle ready dates: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Update package name
  const updatePackageName = useCallback(
    async (packageId: string, name: string) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        
        // Update the package name
        await updateDoc(packageRef, {
          name,
          updated_at: Timestamp.now(),
        })

        // Show success message
        showSuccess('Package name updated successfully')
        
        // Refresh packages to show updated data
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        
        return true
      } catch (err) {
        console.error('Error updating package name:', err)
        showError(`Error updating package name: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Update package description
  const updatePackageDescription = useCallback(
    async (packageId: string, description: string) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        
        // Update the package description
        await updateDoc(packageRef, {
          description,
          updated_at: Timestamp.now(),
        })

        // Show success message
        showSuccess('Package description updated successfully')
        
        // Refresh packages to show updated data
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        
        return true
      } catch (err) {
        console.error('Error updating package description:', err)
        showError(`Error updating package description: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Update vehicle needs in a package
  const updateVehicleNeeds = useCallback(
    async (packageId: string, vehicleId: string, needs: VehicleNeeds) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)
        
        if (!packageDoc.exists()) {
          showError('Package not found')
          return false
        }

        const packageData = packageDoc.data() as TransporterPackage
        const currentNeeds = packageData.vehicle_needs || {}
        
        // Update the needs for the specific vehicle
        const updatedNeeds = {
          ...currentNeeds,
          [vehicleId]: needs
        }
        
        // Update the package with new vehicle needs
        await updateDoc(packageRef, {
          vehicle_needs: updatedNeeds,
          updated_at: Timestamp.now(),
        })

        // Show success message
        showSuccess('Vehicle installation needs updated successfully')
        
        // Refresh packages to show updated data
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        
        return true
      } catch (err) {
        console.error('Error updating vehicle needs:', err)
        showError(`Error updating vehicle needs: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  // Upload photo as admin (for when transporter cannot upload)
  const uploadAdminPhoto = useCallback(
    async (
      packageId: string,
      vehicleId: string,
      angle: string,
      file: File,
      deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair',
      adminUserId: string,
      repairShopIndex?: number
    ) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)
        
        if (!packageDoc.exists()) {
          showError('Package not found')
          return false
        }

        const packageData = packageDoc.data() as TransporterPackage
        
        // Verify vehicle is in package
        if (!packageData.vehicle_ids?.includes(vehicleId)) {
          showError('Vehicle is not in this package')
          return false
        }

        // Determine repair shop index for repair photos if not provided
        let finalRepairShopIndex: number | undefined = repairShopIndex
        if ((deliveryStatus === 'preRepair' || deliveryStatus === 'postRepair') && finalRepairShopIndex === undefined) {
          const interimLocations = packageData.interim_locations?.[vehicleId]
          if (interimLocations) {
            const locations = Array.isArray(interimLocations) ? interimLocations : [interimLocations]
            finalRepairShopIndex = locations.length - 1
          }
        }

        // Create storage path matching transporter API structure
        let storagePath: string
        if ((deliveryStatus === 'preRepair' || deliveryStatus === 'postRepair') && finalRepairShopIndex !== undefined) {
          storagePath = `transporter_packages/${packageId}/photos/${vehicleId}/${deliveryStatus}/shop${finalRepairShopIndex}/${angle}`
        } else {
          storagePath = `transporter_packages/${packageId}/photos/${vehicleId}/${deliveryStatus}/${angle}`
        }

        // Optimize image (convert to webp-like compression)
        let optimizedFile: File
        try {
          optimizedFile = await imageCompression(file, {
            maxSizeMB: 3,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            fileType: 'image/jpeg', // Convert to JPEG for consistency
          })
        } catch (compressionError) {
          console.warn('Image compression failed, using original file:', compressionError)
          optimizedFile = file
        }

        // Upload to Firebase Storage
        if (!storage) {
          throw new Error('Firebase Storage is not initialized')
        }

        const storageRef = ref(storage, storagePath)
        await uploadBytes(storageRef, optimizedFile, {
          contentType: 'image/jpeg',
        })

        // Get download URL
        const url = await getDownloadURL(storageRef)

        // Save photo metadata to Firestore photos subcollection
        const photoMetadata: Omit<TransporterPhoto, 'id'> = {
          packageId,
          vehicleId,
          angle,
          transporterId: packageData.company_id || '',
          userId: adminUserId,
          uploadedAt: Timestamp.now(),
          storagePath,
          uploadedBy: adminUserId,
          format: 'jpeg',
          deliveryStatus,
        }

        if (finalRepairShopIndex !== undefined) {
          photoMetadata.repairShopIndex = finalRepairShopIndex
        }

        await addDoc(collection(db, 'transporter_packages', packageId, 'photos'), photoMetadata)

        // Update package updated_at timestamp
        await updateDoc(packageRef, {
          updated_at: Timestamp.now(),
        })

        // Invalidate queries to refresh UI
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        queryClient.invalidateQueries({ queryKey: ['transporter-photos', packageId, vehicleId] })
        queryClient.invalidateQueries({ queryKey: ['vehicle-photo-status', packageId, vehicleId] })
        // Invalidate the transporter photo status query used by ViewPhotosDialog
        queryClient.invalidateQueries({ queryKey: ['transporter_packages', packageId, 'photos'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages', packageId, 'videos'] })

        showSuccess(`Photo uploaded successfully for ${angle}`)
        return { success: true, url, storagePath }
      } catch (err) {
        console.error('Error uploading admin photo:', err)
        showError(`Error uploading photo: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  const uploadAdminVideo = useCallback(
    async (
      packageId: string,
      vehicleId: string,
      angle: string,
      file: File,
      deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair',
      adminUserId: string,
      repairShopIndex?: number
    ) => {
      try {
        const packageRef = doc(db, 'transporter_packages', packageId)
        const packageDoc = await getDoc(packageRef)
        
        if (!packageDoc.exists()) {
          showError('Package not found')
          return false
        }

        const packageData = packageDoc.data() as TransporterPackage
        
        // Verify vehicle is in package
        if (!packageData.vehicle_ids?.includes(vehicleId)) {
          showError('Vehicle is not in this package')
          return false
        }

        // Determine repair shop index for repair videos if not provided
        let finalRepairShopIndex: number | undefined = repairShopIndex
        if ((deliveryStatus === 'preRepair' || deliveryStatus === 'postRepair') && finalRepairShopIndex === undefined) {
          const interimLocations = packageData.interim_locations?.[vehicleId]
          if (interimLocations) {
            const locations = Array.isArray(interimLocations) ? interimLocations : [interimLocations]
            finalRepairShopIndex = locations.length - 1
          }
        }

        // Create storage path matching transporter API structure
        const sanitize = (str: string) => String(str).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
        const safeAngle = sanitize(angle || "video")
        const fileName = `${safeAngle}.mp4`
        let storagePath: string
        if ((deliveryStatus === 'preRepair' || deliveryStatus === 'postRepair') && finalRepairShopIndex !== undefined) {
          storagePath = `transporter_packages/${packageId}/videos/${vehicleId}/${deliveryStatus}/shop${finalRepairShopIndex}/${fileName}`
        } else {
          storagePath = `transporter_packages/${packageId}/videos/${vehicleId}/${deliveryStatus || 'preDelivery'}/${fileName}`
        }

        // Upload to Firebase Storage
        if (!storage) {
          throw new Error('Firebase Storage is not initialized')
        }

        const storageRef = ref(storage, storagePath)
        await uploadBytes(storageRef, file, {
          contentType: 'video/mp4',
        })

        // Get download URL
        const url = await getDownloadURL(storageRef)

        // Save video metadata to Firestore videos subcollection
        const videoMetadata: any = {
          packageId,
          vehicleId,
          angle: String(angle),
          transporterId: packageData.company_id || '',
          userId: adminUserId,
          uploadedAt: Timestamp.now(),
          storagePath,
          uploadedBy: adminUserId,
          format: 'mp4',
          deliveryStatus: deliveryStatus || 'preDelivery',
        }

        if (finalRepairShopIndex !== undefined) {
          videoMetadata.repairShopIndex = finalRepairShopIndex
        }

        await addDoc(collection(db, 'transporter_packages', packageId, 'videos'), videoMetadata)

        // Update package updated_at timestamp
        await updateDoc(packageRef, {
          updated_at: Timestamp.now(),
        })

        // Invalidate queries to refresh UI
        queryClient.invalidateQueries({ queryKey: ['transporter_packages_with_vehicles'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages'] })
        queryClient.invalidateQueries({ queryKey: ['transporter-photos', packageId, vehicleId] })
        queryClient.invalidateQueries({ queryKey: ['vehicle-photo-status', packageId, vehicleId] })
        // Invalidate the transporter photo status query used by ViewPhotosDialog
        queryClient.invalidateQueries({ queryKey: ['transporter_packages', packageId, 'photos'] })
        queryClient.invalidateQueries({ queryKey: ['transporter_packages', packageId, 'videos'] })

        showSuccess(`Video uploaded successfully for ${angle}`)
        return { success: true, url, storagePath }
      } catch (err) {
        console.error('Error uploading admin video:', err)
        showError(`Error uploading video: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [showSnackbar, queryClient],
  )

  const uploadAdminLegPhoto = useCallback(
    async (
      planId: string,
      legIndex: number,
      angle: string,
      file: File,
      flow: 'preDelivery' | 'postDelivery' = 'preDelivery'
    ) => {
      try {
        const photoBase64 = await fileToBase64(file)
        const result = await apiClient.portal.admin.uploadLegPhoto(
          planId,
          legIndex,
          angle,
          photoBase64,
          flow
        )
        queryClient.invalidateQueries({ queryKey: ['leg_photos_admin', planId, legIndex, flow] })
        queryClient.invalidateQueries({ queryKey: ['leg_photos_status', planId] })
        queryClient.invalidateQueries({ queryKey: ['shipment_plans'] })
        queryClient.invalidateQueries({ queryKey: ['transport_trucks'] })
        showSuccess(`Photo uploaded successfully for ${angle}`)
        return result
      } catch (err) {
        console.error('Error uploading admin leg photo:', err)
        showError(`Error uploading photo: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [queryClient, showSuccess, showError],
  )

  const uploadAdminLegVideo = useCallback(
    async (
      planId: string,
      legIndex: number,
      angle: string,
      file: File,
      flow: 'preDelivery' | 'postDelivery' = 'preDelivery'
    ) => {
      try {
        const result = await apiClient.portal.admin.uploadLegVideo(
          planId,
          legIndex,
          angle,
          file,
          flow
        )
        queryClient.invalidateQueries({ queryKey: ['leg_photos_admin', planId, legIndex, flow] })
        queryClient.invalidateQueries({ queryKey: ['leg_photos_status', planId] })
        queryClient.invalidateQueries({ queryKey: ['shipment_plans'] })
        queryClient.invalidateQueries({ queryKey: ['transport_trucks'] })
        showSuccess(`Video uploaded successfully for ${angle}`)
        return result
      } catch (err) {
        console.error('Error uploading admin leg video:', err)
        showError(`Error uploading video: ${err instanceof Error ? err.message : 'Unknown error'}`)
        throw err
      }
    },
    [queryClient, showSuccess, showError],
  )

  return {
    // Data and loading states
    transporters,
    packages,
    packagesWithVehicles,
    mostRecentPackageByVehicleId,
    transportersWithPackages,
    transporterGroups,
    isLoading,
    isLoadingPackages,

    // Mutations
    createTransporter: createTransporterMutation.mutateAsync,
    createTransporterGroup: createTransporterGroupMutation.mutateAsync,
    updateTransporterGroup: updateTransporterGroupMutation.mutateAsync,
    deleteTransporterGroup: deleteTransporterGroupMutation.mutateAsync,
    createPackage: createPackageMutation.mutateAsync,
    assignPackageToTransporter: assignPackageToTransporterMutation.mutateAsync,
    addTransporterToPackage: addTransporterToPackageMutation.mutateAsync,
    updatePackageStatus: updatePackageStatusMutation.mutateAsync,
    deletePackage,
    deleteTransporter,
    deleteLogisticsUserCompletely,
    deleteTransporterProfileOnly,
    updateTransporter,
    mergeCreateTransporterDocument,
    updateVehicleLocation,
    updateLatestInterimLocation,
    updateSubmissionStatus,
    updatePackageVehicles,
    updateVehiclePriority,
    updateVehicleReady,
    updateMultipleVehicleReadyDates,
    updateVehicleNeeds,
    updateContactStatus,
    updatePackageName,
    updatePackageDescription,

    // Mutation loading states
    isCreatingTransporter: createTransporterMutation.isPending,
    isCreatingPackage: createPackageMutation.isPending,
    isAssigningPackage: assignPackageToTransporterMutation.isPending,
    isAddingTransporterToPackage: addTransporterToPackageMutation.isPending,
    isUpdatingPackageStatus: updatePackageStatusMutation.isPending,

    // Photo functions
    fetchVehiclePhotos,
    fetchVehiclePhotoStatus,
    uploadAdminPhoto,
    uploadAdminVideo,
    uploadAdminLegPhoto,
    uploadAdminLegVideo,


    // Utility functions
    hasVehiclePickupLocation,
    hasVehicleDropoffLocation,
    hasVehicleInterimLocation,
    hasVehicleBothLocations,
    getLocationCompletionCount,
    getVehiclePackageInfo,
    getRepairShopOccupancy,
    archivePackage,
  }
}

export default useAdminTransporter
